import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { CalendarIcon } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { PROFESSION_CATEGORIES, professionsByCategory, categoryOfProfession, specialtySuggestions, ENGAGEMENT_TYPES, COMPENSATION_TYPES, formatDateRange, professionCategoryLabel, localize, PROFESSIONS } from "../lib/meta";
import { useLanguageOptions, useLanguageLabeler, langActive, toggleLanguage } from "../lib/taxonomy";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const emptyInit = {
  name: "", location: "", description: "", dates: "", start_date: null, end_date: null, participation_goal: 0,
  online_required: false, in_person_required: false, languages_needed: [], professional_requirements: "",
  is_opportunity: false, required_profession: "", required_profession_group: "", required_specialty: "", required_credential_status: "any", min_experience: null,
  engagement_type: "", compensation_type: "to_be_discussed", professionals_needed: null,
  required_languages: [], application_deadline: null,
};

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => (d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : null);
const fromISO = (s) => (s ? new Date(s + "T00:00:00") : undefined);

export function InitiativeEditor({ open, onOpenChange, initiative, onSaved }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState(emptyInit);
  const langOptions = useLanguageOptions();
  const langLabel = useLanguageLabeler();
  const [saving, setSaving] = useState(false);
  const [dateMode, setDateMode] = useState("single");
  const [dateOpen, setDateOpen] = useState(false);

  useEffect(() => {
    if (open) {
      const src = { ...emptyInit, ...(initiative || {}) };
      // Backward-compat: older opportunities may have a profession but no category; infer it.
      if (src.required_profession && !src.required_profession_group) {
        src.required_profession_group = categoryOfProfession(src.required_profession) || "";
      }
      setForm(src);
      setDateMode(src.end_date && src.end_date !== src.start_date ? "range" : "single");
    }
  }, [open, initiative]);

  const set = (k, v, opts) => setForm((f) => {
    const next = { ...f, [k]: v };
    (opts?.reset || []).forEach((rk) => { next[rk] = ""; });
    return next;
  });
  const toggleLang = (l) => setForm((f) => ({ ...f, languages_needed: toggleLanguage(f.languages_needed, l) }));
  const toggleReqLang = (l) => setForm((f) => ({ ...f, required_languages: toggleLanguage(f.required_languages, l) }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, participation_goal: parseInt(form.participation_goal) || 0 };
      payload.dates = payload.end_date && payload.end_date !== payload.start_date
        ? `${payload.start_date} – ${payload.end_date}` : (payload.start_date || "");
      let data;
      if (!initiative) ({ data } = await api.post("/initiatives", payload));
      else ({ data } = await api.put(`/initiatives/${initiative.id}`, payload));
      toast.success(t("organizer.saved"));
      onOpenChange(false);
      onSaved?.(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="initiative-editor">
        <DialogHeader>
          <DialogTitle>{initiative ? t("organizer.edit_title") : t("organizer.create_title")}</DialogTitle>
          <DialogDescription>{t("initiatives.requirements")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div><Label>{t("organizer.name")}</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="init-name" /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>{t("initiatives.location")}</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="init-location" /></div>
            <div>
              <Label>{t("initiatives.dates")}</Label>
              <div className="mb-2 mt-1 flex gap-2">
                {[["single", t("initiatives.single_day")], ["range", t("initiatives.date_range")]].map(([m, lbl]) => (
                  <button type="button" key={m} onClick={() => setDateMode(m)} data-testid={`date-mode-${m}`}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${dateMode === m ? "border-terracotta bg-terracotta-light text-terracotta" : "border-border text-slateink/70"}`}>{lbl}</button>
                ))}
              </div>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className="w-full justify-start font-normal" data-testid="init-dates-trigger">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {form.start_date ? formatDateRange(form.start_date, form.end_date, lang) : t("initiatives.pick_dates")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" data-testid="init-dates-popover">
                  {dateMode === "single" ? (
                    <Calendar mode="single" selected={fromISO(form.start_date)}
                      onSelect={(d) => { setForm((f) => ({ ...f, start_date: toISO(d), end_date: null })); setDateOpen(false); }} />
                  ) : (
                    <Calendar mode="range" numberOfMonths={1}
                      selected={{ from: fromISO(form.start_date), to: fromISO(form.end_date) }}
                      onSelect={(r) => setForm((f) => ({ ...f, start_date: toISO(r?.from), end_date: toISO(r?.to) }))} />
                  )}
                  {form.start_date && (
                    <div className="border-t p-2">
                      <Button type="button" variant="ghost" size="sm" data-testid="clear-dates-btn"
                        onClick={() => setForm((f) => ({ ...f, start_date: null, end_date: null }))}>{t("initiatives.clear_dates")}</Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div><Label>{t("initiatives.description")}</Label><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="init-description" /></div>
          <div><Label>{t("initiatives.goal")}</Label><Input type="number" min="0" value={form.participation_goal} onChange={(e) => set("participation_goal", e.target.value)} data-testid="init-goal" /></div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={form.online_required} onCheckedChange={(v) => set("online_required", !!v)} data-testid="init-online" /><span className="text-sm">{t("common.online")}</span></label>
            <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={form.in_person_required} onCheckedChange={(v) => set("in_person_required", !!v)} data-testid="init-in-person" /><span className="text-sm">{t("common.in_person")}</span></label>
          </div>
          <div>
            <Label>{t("initiatives.languages_needed")}</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {langOptions.map((l) => (
                <button type="button" key={l.value} onClick={() => toggleLang(l.value)} data-testid={`init-lang-${l.value}`} className={`rounded-full border px-3 py-1.5 text-sm ${langActive(form.languages_needed, l.value) ? "border-terracotta bg-terracotta-light text-terracotta" : "border-border text-slateink/70"}`}>{langLabel(l.value, lang)}</button>
              ))}
            </div>
          </div>
          <div><Label>{t("initiatives.requirements")}</Label><Textarea rows={2} value={form.professional_requirements} onChange={(e) => set("professional_requirements", e.target.value)} data-testid="init-requirements" /></div>

          <div className="rounded-xl border border-border p-4">
            <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={form.is_opportunity} onCheckedChange={(v) => set("is_opportunity", !!v)} data-testid="init-is-opportunity" /><span className="text-sm font-medium">{t("initiatives.is_opportunity_label")}</span></label>
            {form.is_opportunity && (
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label>{t("initiatives.category_sought")}</Label>
                  <Select value={form.required_profession_group || "any"} onValueChange={(v) => set("required_profession_group", v === "any" ? "" : v, { reset: ["required_profession", "required_specialty"] })}>
                    <SelectTrigger data-testid="init-req-group"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="any">{t("common.any")}</SelectItem>{PROFESSION_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{professionCategoryLabel(c.value, lang)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {form.required_profession_group && (
                  <div className="sm:col-span-2">
                    <Label>{t("opportunities.required_profession")}</Label>
                    <Select value={form.required_profession || "any"} onValueChange={(v) => set("required_profession", v === "any" ? "" : v, { reset: ["required_specialty"] })}>
                      <SelectTrigger data-testid="init-req-profession"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="any">{t("common.any")}</SelectItem>{professionsByCategory(form.required_profession_group).map((p) => <SelectItem key={p.value} value={p.value}>{localize(PROFESSIONS, p.value, lang)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {form.required_profession && specialtySuggestions(form.required_profession, lang).length > 0 && (
                  <div className="sm:col-span-2">
                    <Label>{t("initiatives.specialty_optional")}</Label>
                    <Select value={form.required_specialty || "any"} onValueChange={(v) => set("required_specialty", v === "any" ? "" : v)}>
                      <SelectTrigger data-testid="init-req-specialty"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="any">{t("common.any")}</SelectItem>{specialtySuggestions(form.required_profession, lang).map(({ key, label }) => <SelectItem key={key} value={key}>{label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>{t("opportunities.engagement_type")}</Label>
                  <Select value={form.engagement_type || "any"} onValueChange={(v) => set("engagement_type", v === "any" ? "" : v)}>
                    <SelectTrigger data-testid="init-req-engagement"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="any">{t("common.any")}</SelectItem>{ENGAGEMENT_TYPES.map((e) => <SelectItem key={e.value} value={e.value}>{localize(ENGAGEMENT_TYPES, e.value, lang)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("opportunities.compensation_type")}</Label>
                  <Select value={form.compensation_type || "to_be_discussed"} onValueChange={(v) => set("compensation_type", v)}>
                    <SelectTrigger data-testid="init-req-compensation"><SelectValue /></SelectTrigger>
                    <SelectContent>{COMPENSATION_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{localize(COMPENSATION_TYPES, c.value, lang)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{t("opportunities.professionals_needed")}</Label><Input type="number" min="1" value={form.professionals_needed ?? ""} onChange={(e) => set("professionals_needed", e.target.value === "" ? null : Number(e.target.value))} data-testid="init-req-needed" /></div>
                <div><Label>{t("opportunities.min_experience")}</Label><Input type="number" min="0" value={form.min_experience ?? ""} onChange={(e) => set("min_experience", e.target.value === "" ? null : Number(e.target.value))} data-testid="init-req-experience" /></div>
                <div className="sm:col-span-2">
                  <Label>{t("opportunities.req_languages_field")}</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {langOptions.map((l) => (
                      <button type="button" key={l.value} onClick={() => toggleReqLang(l.value)} data-testid={`init-req-lang-${l.value}`}
                        className={`rounded-full border px-3 py-1.5 text-sm ${langActive(form.required_languages, l.value) ? "border-terracotta bg-terracotta-light text-terracotta" : "border-border text-slateink/70"}`}>{langLabel(l.value, lang)}</button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label>{t("opportunities.deadline_field")}</Label>
                  <Input type="date" value={form.application_deadline || ""} onChange={(e) => set("application_deadline", e.target.value || null)} data-testid="init-req-deadline" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer sm:col-span-2"><Checkbox checked={form.required_credential_status === "verified"} onCheckedChange={(v) => set("required_credential_status", v ? "verified" : "any")} data-testid="init-req-verified" /><span className="text-sm">{t("opportunities.verified_required")}</span></label>
              </div>
            )}
          </div>
          <Button type="submit" disabled={saving} className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="save-initiative-btn">{saving ? t("common.saving") : t("common.save")}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
